import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faPinterestP,
  faTiktok,
  faTwitter,
  faYoutube
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from 'config';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useParams, usePathname, useSearchParams } from 'next/navigation';

import {
  HEBA_PAGE_ROUTE,
  getPlanEventWithHebaURL
} from '@/services/planEventWithHeba.service';
import { staticMediaStoreBaseURL } from 'config/common';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';
import { Text } from '../../nuclei';

// TODO: twitter, pinterest account are not active, Need to ask, if we can remove
const socialMediaIconList = [
  {
    url: 'https://www.linkedin.com/company/hafla-official',
    faIcon: faLinkedinIn,
    isVisible: true,
    color: '#DF7447'
  },
  {
    url: 'https://www.youtube.com/channel/UCaUo41TfdsE8Lr_OCb-modg',
    faIcon: faYoutube,
    isVisible: true,
    color: '#E56A66'
  },
  {
    url: 'https://www.facebook.com/hafla.Official',
    faIcon: faFacebookF,
    isVisible: true,
    color: '#E2627E'
  },
  {
    url: 'https://www.instagram.com/hafla.official',
    faIcon: faInstagram,
    isVisible: true,
    color: '#DB589A'
  },
  {
    url: 'http://www.tiktok.com/@haflaofficial',
    faIcon: faTiktok,
    isVisible: true,
    color: '#DD5489'
  },
  {
    url: 'https://twitter.com/haflaOfficial',
    faIcon: faTwitter,
    isVisible: false
  },
  {
    url: 'https://in.pinterest.com/HaflaOfficial',
    faIcon: faPinterestP,
    isVisible: false
  }
];

const exploreSectionData = [
  { href: ROUTE_CONFIG.HOME_PAGE.label, textKey: 'home' },
  { href: ROUTE_CONFIG.CORPORATE_EVENTS.label, textKey: 'corporate' },
  { href: ROUTE_CONFIG.WEDDING_EVENTS.label, textKey: 'weddings' },
  { href: ROUTE_CONFIG.EVENTS_CELEBRATION.label, textKey: 'others' }
];

const policyData = [
  { href: ROUTE_CONFIG.TERMS_AND_CONDITION.label, textKey: 'terms' },
  { href: ROUTE_CONFIG.PRIVACY_POLICY.label, textKey: 'privacyPolicy' },
  { href: ROUTE_CONFIG.ACCEPTABLE_USE_POLICY.label, textKey: 'usePolicy' }
];

export const STATS = [
  {
    label: 'Events Delivered',
    value: '20,000+'
  },
  {
    label: 'Average Rating',
    value: '4.8★'
  },
  {
    label: 'Verified Partners',
    value: '500+'
  },
  {
    label: 'On-time Execution',
    value: '99%'
  }
];

const dividerClassName =
  'md:after:absolute md:after:right-0 md:after:top-1/2 md:after:-translate-y-1/2 md:after:h-12 md:after:w-px md:after:bg-white/40';

const companySectionData = [
  {
    href: getPageURL({
      pageName: ROUTE_CONFIG.ABOUT.label
    }),
    textKey: 'about',
    additionalClass: ' font-thin text-sm'
  },
  {
    href: 'https://www.hafla.com/blog/',
    textKey: 'blog',
    additionalClass: ' text-xs'
  },
  {
    href: getPageURL({
      pageName: ROUTE_CONFIG.CONTACT_US.label
    }),
    textKey: 'contact',
    additionalClass: ' text-xs'
  }
];

const SocialMediaIcon = ({
  url,
  faIcon,
  iconClass = '',
  className = '',
  anchorStyle = {},
  iconStyle = {}
}) => (
  <a
    key={url}
    target='_blank'
    href={url}
    className={className}
    style={anchorStyle}
    rel='noreferrer'
  >
    <li className='flex items-center justify-center'>
      <FontAwesomeIcon
        icon={faIcon}
        className={iconClass}
        style={iconStyle}
      />
    </li>
  </a>
);

const ExploreSection = ({ textKey, href, translate }) => (
  <li className='mt-1.5 md:mt-3 text-xs md:text-base font-extralight max-w-max leading-4 text-white hover:text-white active:text-white'>
    <a
      href={getPageURL({
        pageName: href
      })}
    >
      {translate(textKey)}
    </a>
  </li>
);

const CompanySection = ({ textKey, href, additionalClass = '', translate }) => {
  const className = `mt-1.5 md:mt-3 md:text-base font-extralight max-w-max leading-4 text-white hover:text-white active:text-white ${additionalClass}`;
  return (
    <li className={className}>
      <a href={href}>{translate(textKey)}</a>
    </li>
  );
};

const Footer = ({ showFooterBanner = false }) => {
  const translate = useTranslations('footer');
  const [isMobile] = useIsMobile();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const planEventWithHebaURL = getPlanEventWithHebaURL({
    pageRoute: HEBA_PAGE_ROUTE.WHATSAPP,
    params,
    pathname,
    searchParams
  });

  return (
    <footer className='w-full bg-black text-white'>
      {showFooterBanner && (
        <>
          {' '}
          <div className='w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16 text-center max-w-4xl mx-auto'>
            <div
              className={`text-[1.5rem] xs:text-[1.75rem] sm:text-[1.75rem] md:text-[3rem] lg:text-[4.5rem] xl:text-[4.5rem] font-black leading-[1.25] text-stroke-normal xs:text-stroke-normal sm:text-stroke-normal md:text-stroke-bold tracking-wide`}
            >
              Your Event Is Just A <br className='block md:hidden' />
              <Text
                {...{
                  className: 'bg-brand-gradient bg-clip-text text-transparent',
                  content: 'Click Away!',
                  HtmlTag: 'span'
                }}
              />
            </div>
            <Text
              {...{
                className:
                  'text-xs xs:text-sm sm:text-sm md:text-lg lg:text-lg mt-3 xs:mt-4 sm:mt-4 mx-auto',
                content: `From ideas to execution, everything you need to celebrate—one platform.`
              }}
            />
            <Link
              className={`mt-6 xs:mt-8 sm:mt-6 inline-flex items-center justify-center gap-1 xs:gap-1.5 sm:gap-1 md:gap-2 rounded-lg px-3 xs:px-4 sm:px-3 md:px-6 py-2.5 xs:py-2.5 sm:py-3 md:py-4 text-xs xs:text-sm sm:text-sm md:text-base font-semibold shadow-lg bg-brand-gradient text-white hover:opacity-90 transition-opacity w-full xs:w-auto sm:w-auto`}
              href={planEventWithHebaURL}
              rel='noopener noreferrer nofollow'
              target='_self'
            >
              <Image
                alt='WhatsApp'
                className='w-4 h-4 sm:w-5 sm:h-5'
                height={20}
                src={`${staticMediaStoreBaseURL}/icons/whatsapp-green.svg`}
                width={20}
              />
              Plan with Hafla AI
            </Link>
          </div>
          <div className='px-3 xs:px-4 md:py-10 pb-10 xs:pb-12'>
            <div className='max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 text-center gap-y-8'>
              {STATS.map(({ label, value }, index) => (
                <div
                  className={`relative flex flex-col items-center px-3 md:px-6 ${
                    index !== STATS.length - 1 ? dividerClassName : ''
                  }`}
                  key={index}
                >
                  <Text
                    className='bg-brand-gradient bg-clip-text text-transparent text-xl xs:text-2xl md:text-5xl font-extrabold'
                    content={value}
                  />

                  <Text
                    className='text-sm md:text-base mt-2 md:mt-3 text-white'
                    content={label}
                  />
                </div>
              ))}
            </div>
          </div>
          <hr className='border-t border-white/20 w-full max-w-7xl mx-auto' />
        </>
      )}
      <div className='p-5 md:px-20 md:py-15 footer-padding-bottom'>
        <div className='flex flex-wrap'>
          <div className='w-full md:w-1/5'>
            <Link
              href={getPageURL({
                pageName: ROUTE_CONFIG.HOME_PAGE.label
              })}
              aria-label='Go home'
              title='Hafla'
              className='items-center'
            >
              <Image
                width={104}
                height={34}
                src={`${staticMediaStoreBaseURL}/logos/hafla_new.svg`}
                alt='Hafla'
                className='inline-block max-w-17.5 md:max-w-26'
                style={{
                  width: isMobile ? '70px' : '104px',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </Link>

            <div className='w-full mt-2 md:mt-3 font-normal text-xs md:text-lg text-white'>
              {translate('eventsSimplified')}
            </div>

            <div className='md:justify-start pt-10 pb-4 hidden md:block'>
              <ul className='list-none justify-start grid grid-flow-row grid-cols-5 grid-rows-2 gap-4 w-max'>
                {socialMediaIconList
                  .filter((item) => item.isVisible)
                  .map(({ url, faIcon, color }, index) => (
                    <SocialMediaIcon
                      key={index}
                      url={url}
                      faIcon={faIcon}
                      iconStyle={{ color }}
                      anchorStyle={{ borderColor: color }}
                      iconClass={'text-cinnabar'}
                      className={`p-2 h-7.5 w-7.5 rounded-full border text-white text-sm hover:text-about hover:bg-white md:hover:animate-spin box-border transition-all`}
                    />
                  ))}
              </ul>
            </div>
          </div>

          <div className='w-2/4 md:w-1/5 pt-4 md:pt-0'>
            <p className='text-xs font-bold md:text-lg font-medium uppercase text-white mb-3 md:mb-4'>
              {translate('company')}
            </p>
            <hr className='hr-line' />
            <ul className='pt:2 md:pt-3'>
              {companySectionData.map(
                ({ href, textKey, additionalClass }, index) => (
                  <CompanySection
                    key={index}
                    translate={translate}
                    {...{ href, textKey, additionalClass }}
                  />
                )
              )}
            </ul>
          </div>

          <div className='w-2/4 md:w-1/5 pt-4 md:pt-0'>
            <p className='text-xs md:text-lg font-medium text-white uppercase mb-3'>
              {translate('explore')}
            </p>
            <hr className='hr-line' />
            <ul className='pt:2 md:pt-3'>
              {exploreSectionData.map(({ href, textKey }, index) => (
                <ExploreSection
                  key={index}
                  translate={translate}
                  {...{ href, textKey }}
                />
              ))}
            </ul>
          </div>
          <div className='w-2/4 md:w-1/5 pt-4 md:pt-0'>
            <p className='text-xs md:text-lg font-medium text-white uppercase mb-3'>
              {translate('policy')}
            </p>
            <hr className='hr-line' />
            <ul className='pt:2 md:pt-3'>
              {policyData.map(({ href, textKey }, index) => (
                <ExploreSection
                  key={index}
                  translate={translate}
                  {...{ href, textKey }}
                />
              ))}
            </ul>
          </div>
          <div className='w-2/4 md:w-1/5 pt-4 md:pt-0'>
            <p className='text-xs md:text-lg font-medium text-white uppercase mb-3'>
              {translate('officeAddress')}
            </p>
            <hr className='hr-line' />
            <div className='pt:2 md:pt-3'>
              <div className='mt-1.5 md:mt-3 md:text-base font-extralight max-w-max leading-4 text-white hover:text-white active:text-white text-xs'>
                2nd Floor Nook Office ONE JLT One Business Centre Dubai - United
                Arab Emirates
              </div>
              <div
                className='flex mt-1.5 md:mt-3 gap-2 cursor-pointer'
                onClick={() =>
                  window.open(config.haflaGooglePinLocation, '_blank')
                }
              >
                <div className='md:text-base font-extralight max-w-max leading-4 bg-light-brand bg-clip-text text-transparent font-bold text-xs'>
                  Directions
                </div>
                <Image
                  alt='direction'
                  height={isMobile ? 16 : 24}
                  width={isMobile ? 16 : 24}
                  src={`${staticMediaStoreBaseURL}/icons/direction.svg`}
                  style={{
                    width: isMobile ? '16px' : '24px',
                    height: isMobile ? '16px' : '24px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          </div>
          <div className='w-full md:w-1/4'></div>
        </div>

        <div className='items-center justify-center md:justify-start pt-8 pb-1 space-x-4 md:hidden'>
          <ul className='list-none space-x-4 flex justify-center items-center'>
            {socialMediaIconList
              .filter((item) => item.isVisible)
              .map(({ url, faIcon, color }, index) => (
                <SocialMediaIcon
                  key={index}
                  url={url}
                  faIcon={faIcon}
                  iconStyle={{ color }}
                  anchorStyle={{ borderColor: color }}
                  iconClass={'text-cinnabar'}
                  className={`p-2 h-7.5 w-7.5 rounded-full border text-white text-sm hover:text-about hover:bg-white md:hover:animate-spin box-border transition-all`}
                />
              ))}
          </ul>
        </div>

        <div className='mt-4 md:mt-10'>
          <p className='text-xs md:text-base text-center md:text-left text-dim-gray'>
            {` ${translate('copyright')} `}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
